import { FC } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Button } from '$components/elements/button';
import { Headline } from '$components/elements/headline';
import { PaymentIcons } from '$components/elements/payment-icons/payment-icons';
import { SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { Flex } from '$components/layouts';
import { B2CBasketType } from '$constants/basket';
import { checkoutPageUrl } from '$constants/urls';
import { useAppEvents, useTranslation } from '$hooks';
import { mq } from '$lib/helpers';
import { ICalculatedBasket, IPaymentIPaymentMethod, IShippingType } from '$models';
import { AppContentContainer } from '~/modules/app';
import { BasketOrderSummary } from './basket-order-summary';

type Props = {
    basket?: ICalculatedBasket;
    shipping?: IShippingType;
    onNotPossibleClick?: () => void;
    paymentIcons?: IPaymentIPaymentMethod[];
};

const iconSize = 35;

export const BasketSummary: FC<Props> = ({
    basket,
    shipping,
    paymentIcons,
    onNotPossibleClick,
}: Props): JSX.Element => {
    const { translate } = useTranslation();
    const router = useRouter();
    const { appShopMore } = useAppEvents();

    return (
        <>
            <BasketSummaryWrapper flex column>
                <Headline as="h2" size={3}>
                    {translate('checkout.orderSummary.basketHeader')}
                </Headline>
                <BasketOrderSummary basket={basket} shipping={shipping} />
                {basket?.basketType === B2CBasketType.NotPossible ? (
                    <>
                        <SummaryButton variant="tertiary" width="expansive" onClick={onNotPossibleClick}>
                            {translate('checkout.checkoutBasket.toCheckoutButton')}
                        </SummaryButton>
                        <NotPossibleContainer>
                            <Text p fontWeight="Bold">
                                {translate('checkout.orderSummary.adjustBasketHeader')}
                            </Text>
                            <NoShippingTypesMessage p>
                                {translate('checkout.general.noShippingTypesWarningMessage')}
                            </NoShippingTypesMessage>
                            <Button onClick={onNotPossibleClick} variant="link">
                                <Text fontWeight="Bold">{translate('checkout.general.noShippingNeedHelp')}</Text>
                            </Button>
                        </NotPossibleContainer>
                    </>
                ) : (
                    <SummaryButton
                        disabled={!!basket?.invalidLines?.length}
                        onClick={() => router.push(checkoutPageUrl)}
                        width="expansive"
                    >
                        {translate('checkout.checkoutBasket.toCheckoutButton')}
                    </SummaryButton>
                )}
                <AppContentContainer onlyShowInApp>
                    <ShopMoreButton variant="custom" onClick={appShopMore}>
                        <Text>{translate('app.basket.shopMoreButton')}</Text>
                        <SvgIcon size={20} svg="chevronRight" />
                    </ShopMoreButton>
                </AppContentContainer>
                <AppContentContainer>
                    <PaymentIconsHeader>{translate('checkout.orderSummary.weAcceptHeader')}</PaymentIconsHeader>
                    <PaymentIcons paymentIcons={paymentIcons ?? []} iconHeight={iconSize} iconWidth={iconSize} />
                </AppContentContainer>
                <VoucherText>{translate('checkout.orderSummary.gotAVoucherMessage')}</VoucherText>
            </BasketSummaryWrapper>
        </>
    );
};

const SummaryButton = styled(Button)(({ theme }) => ({
    marginTop: theme.space[5],
    marginBottom: theme.space[5],
}));

const VoucherText = styled.div(({ theme }) => ({
    color: theme.colors.primaryGrey,
}));

const BasketSummaryWrapper = styled(Flex)(({ theme }) => ({
    alignSelf: 'flex-start',
    border: theme.general.border,
    borderRadius: theme.general.borderRadius,
    padding: `${theme.space[5]} ${theme.space[3]}`,
    marginBottom: theme.space[3],
    [mq('md')]: {
        padding: `${theme.space[9]} ${theme.space[7]}`,
    },
}));

const NotPossibleContainer = styled.div(({ theme }) => ({
    position: 'relative',
    backgroundColor: theme.colors.red,
    color: theme.colors.white,
    padding: `${theme.space[5]} ${theme.space[7]}`,
    marginTop: '15px',
    marginBottom: theme.space[5],

    '&:before': {
        content: '""',
        borderLeft: '15px solid transparent',
        borderRight: '15px solid transparent',
        borderBottom: `15px solid ${theme.colors.primaryTheme}`,
        position: 'absolute',
        top: '-15px',
        marginLeft: 'auto',
        marginRight: 'auto',
        left: 0,
        right: 0,
        width: 0,
    },
}));

const NoShippingTypesMessage = styled(Text)(({ theme }) => ({
    marginRight: theme.space[1],
    fontsize: theme.fontSizes.sm,
}));

const PaymentIconsHeader = styled.div(({ theme }) => ({
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.space[3],
}));

const ShopMoreButton = styled(Button)(({ theme }) => ({
    alignItems: 'center',
    marginBottom: theme.space[3],
}));
